import type { SvgIconComponent } from '@mui/icons-material';
import {
    FormControl,
    FormControlLabel,
    FormControlProps,
    FormHelperText,
    Skeleton,
    Stack,
    Switch,
    SwitchProps,
    Typography,
    styled,
} from '@mui/material';

const SIZES = {
    small: {
        width: 39,
        height: 24,
        switchBase: {
            margin: '1.4px 0 0 0',
            translate: '16px',
        },
        thumb: {
            width: 21,
            height: 21,
        },
        icons: {
            size: 12,
            left: 13,
            right: 6,
        },
    },
    medium: {
        width: 42,
        height: 27,
        switchBase: {
            margin: '2px 0 0 0',
            translate: '16px',
        },
        thumb: {
            width: 23,
            height: 23,
        },
        icons: {
            size: 12,
            left: 14,
            right: 7,
        },
    },
    large: {
        width: 72,
        height: 36,
        switchBase: {
            margin: '3px 4px',
            translate: '32px',
        },
        thumb: {
            width: 30,
            height: 30,
        },
        icons: {
            size: 17,
            left: 19,
            right: 11,
        },
    },
};

const IOSSwitch = styled(
    (
        props: SwitchProps & {
            topLabel?: React.ReactNode;
            formControlProps?: FormControlProps;
            helperText?: React.ReactNode;
            loading?: boolean;
            LeftIcon?: SvgIconComponent;
            RightIcon?: SvgIconComponent;
            noShadow?: boolean;
        }
    ) => {
        const { topLabel, formControlProps, loading, LeftIcon, RightIcon, noShadow, ...switchProps } = props;

        const switchComponent =
            !LeftIcon && !RightIcon ? (
                <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...switchProps} />
            ) : (
                <Stack
                    direction="row"
                    alignItems="center"
                    position="relative"
                    spacing={1}
                    sx={{
                        '& .ios-switch-left-icon, & .ios-switch-right-icon': {
                            color: 'neutral-500',
                            transition: 'color 220ms ease-in-out',
                        },
                        '&:not(:has(.MuiSwitch-switchBase.Mui-checked)) .ios-switch-left-icon': {
                            color: 'primary.main',
                        },
                        '&:has(.MuiSwitch-switchBase.Mui-checked) .ios-switch-right-icon': {
                            color: 'primary.main',
                        },
                    }}
                >
                    {LeftIcon && (
                        <LeftIcon
                            className="ios-switch-left-icon absolute z-50 pointer-events-none"
                            sx={{
                                fontSize: SIZES[props.size ?? 'medium'].icons.size,
                                left: SIZES[props.size ?? 'medium'].icons.left,
                            }}
                        />
                    )}
                    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...switchProps} />
                    {RightIcon && (
                        <RightIcon
                            className="ios-switch-right-icon absolute z-50 pointer-events-none"
                            sx={{
                                fontSize: SIZES[props.size ?? 'medium'].icons.size,
                                right: SIZES[props.size ?? 'medium'].icons.right,
                            }}
                        />
                    )}
                </Stack>
            );

        const LoadingWrapper = loading ? (
            <Skeleton variant="rounded" className="rounded-full">
                {switchComponent}
            </Skeleton>
        ) : (
            switchComponent
        );

        return topLabel ? (
            <FormControl disabled={loading} {...formControlProps}>
                <FormControlLabel
                    label={
                        <Typography
                            fontFamily="museo-sans"
                            fontSize={16}
                            fontWeight={600}
                            mb="10px"
                            color="neutral-900"
                        >
                            {topLabel}
                            {props.required ? (
                                <Typography component="span" color="signal-warning">
                                    {' '}
                                    *
                                </Typography>
                            ) : null}
                        </Typography>
                    }
                    control={LoadingWrapper}
                    labelPlacement="top"
                    className="items-start m-0"
                    required={false}
                />
                {props.helperText && (
                    <FormHelperText error={formControlProps?.error} className="mt-[4px]">
                        {props.helperText}
                    </FormHelperText>
                )}
            </FormControl>
        ) : (
            <>
                {LoadingWrapper}
                {props.helperText && (
                    <FormHelperText error={formControlProps?.error} className="mt-[4px]">
                        {props.helperText}
                    </FormHelperText>
                )}
            </>
        );
    }
)(({ theme, size = 'medium', LeftIcon, RightIcon, noShadow }) => ({
    width: SIZES[size].width,
    height: SIZES[size].height,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: SIZES[size].switchBase.margin,
        paddingLeft: 1,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: `translateX(${SIZES[size].switchBase.translate})`,
            color: '#fff',
            '& + .MuiSwitch-track': {
                opacity: 1,
                border: 0,
                backgroundColor: RightIcon || LeftIcon ? '#E9E9EA' : undefined,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
        },
    },
    '.MuiSwitch-thumb': {
        width: SIZES[size].thumb.width,
        height: SIZES[size].thumb.height,
        boxShadow: noShadow ? 'none' : undefined,
    },
    '& .MuiSwitch-track': {
        borderRadius: 50,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

export { IOSSwitch };
